import React, { Fragment } from 'react';
import { Accordion } from './ui/Accordion';
import { Configuration } from '../utils/types';
interface BOMPriceProps {
  configuration: Configuration;
  showVat: boolean;
}
export function BOMPrice({
  configuration,
  showVat
}: BOMPriceProps) {
  // Sample BOM data
  const bomItems = [{
    brand: 'Roth',
    vvsNo: '146B7078',
    description: 'Roth gulvvarmefordeler 6 afgr.',
    quantity: 2,
    price: 4250,
    total: 8500,
    eta: 'På lager'
  }, {
    brand: 'Roth',
    vvsNo: '146B7094',
    description: 'Roth X-PERT S5® 16 mm',
    quantity: 240,
    price: 18.5,
    total: 4440,
    eta: 'På lager'
  }, {
    brand: 'CompA',
    vvsNo: '403B5512',
    description: 'CompA WiFi-termostat M30',
    quantity: 8,
    price: 1250,
    total: 10000,
    eta: '2-3 uger'
  }, {
    brand: 'CompB',
    vvsNo: '506C8834',
    description: 'CompB cirkulationspumpe',
    quantity: 1,
    price: 3750,
    total: 3750,
    eta: 'På lager'
  }, {
    brand: 'Roth',
    vvsNo: '146B7112',
    description: 'Roth adaptere M30→M28',
    quantity: 8,
    price: 85,
    total: 680,
    eta: 'På lager'
  }];
  // Group by brand
  const groupedByBrand = bomItems.reduce((acc, item) => {
    if (!acc[item.brand]) {
      acc[item.brand] = [];
    }
    acc[item.brand].push(item);
    return acc;
  }, {} as Record<string, typeof bomItems>);
  // Calculate subtotals by brand
  const brandTotals = Object.keys(groupedByBrand).map(brand => {
    return {
      brand,
      total: groupedByBrand[brand].reduce((sum, item) => sum + item.total, 0)
    };
  });
  // Calculate grand total
  const grandTotal = brandTotals.reduce((sum, brand) => sum + brand.total, 0);
  // Shipping costs
  const shippingCosts = [{
    brand: 'Roth',
    cost: 0
  }, {
    brand: 'CompA',
    cost: 250
  }, {
    brand: 'CompB',
    cost: 150
  }];
  const totalShipping = shippingCosts.reduce((sum, item) => sum + item.cost, 0);
  // Apply VAT if needed
  const vatRate = 0.25; // 25% VAT
  const applyVat = (amount: number) => showVat ? amount * (1 + vatRate) : amount;
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('da-DK', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount) + ' kr';
  };
  return <Accordion title="Stykliste & Pris" data-id="element-4">
      <div className="overflow-x-auto" data-id="element-5">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-6">
          <thead data-id="element-7">
            <tr data-id="element-8">
              <th className="px-3 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9">
                Brand
              </th>
              <th className="px-3 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10">
                VVS-nr.
              </th>
              <th className="px-3 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11">
                Beskrivelse
              </th>
              <th className="px-3 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12">
                Antal
              </th>
              <th className="px-3 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13">
                Stk. pris
              </th>
              <th className="px-3 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14">
                Total
              </th>
              <th className="px-3 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15">
                ETA
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-16">
            {Object.keys(groupedByBrand).map(brand => <Fragment key={brand} data-id="element-17">
                {groupedByBrand[brand].map((item, index) => <tr key={`${brand}-${index}`} className="hover:bg-gray-50" data-id="element-18">
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900" data-id="element-19">
                      {item.brand}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-20">
                      {item.vvsNo}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900" data-id="element-21">
                      {item.description}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-22">
                      {item.quantity}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-23">
                      {formatCurrency(applyVat(item.price))}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-24">
                      {formatCurrency(applyVat(item.total))}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-25">
                      {item.eta}
                    </td>
                  </tr>)}
                {/* Brand subtotal */}
                <tr className="bg-gray-50" data-id="element-26">
                  <td colSpan={5} className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-700 text-right" data-id="element-27">
                    Subtotal {brand}:
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 text-right" data-id="element-28">
                    {formatCurrency(applyVat(brandTotals.find(b => b.brand === brand)?.total || 0))}
                  </td>
                  <td data-id="element-29"></td>
                </tr>
              </Fragment>)}
            {/* Shipping costs */}
            {shippingCosts.map((item, index) => <tr key={`shipping-${index}`} className="hover:bg-gray-50" data-id="element-30">
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900" data-id="element-31">
                  {item.brand}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-32">
                  -
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900" data-id="element-33">
                  Fragt (faktureret)
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-34">
                  1
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-35">
                  {formatCurrency(applyVat(item.cost))}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-36">
                  {formatCurrency(applyVat(item.cost))}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-37">
                  -
                </td>
              </tr>)}
            {/* Grand total */}
            <tr className="bg-gray-100" data-id="element-38">
              <td colSpan={5} className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right" data-id="element-39">
                Total {showVat ? 'inkl. moms' : 'ekskl. moms'}:
              </td>
              <td className="px-3 py-3 whitespace-nowrap text-base font-medium text-gray-900 text-right" data-id="element-40">
                {formatCurrency(applyVat(grandTotal + totalShipping))}
              </td>
              <td data-id="element-41"></td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* Discount chips */}
      <div className="mt-4 flex flex-wrap gap-2" data-id="element-42">
        <div className="bg-green-50 px-3 py-1.5 rounded-lg text-sm text-green-700" data-id="element-43">
          Årsrabat 5%
        </div>
        <div className="bg-green-50 px-3 py-1.5 rounded-lg text-sm text-green-700" data-id="element-44">
          Fast rabat 2.500 kr
        </div>
        <div className="bg-green-50 px-3 py-1.5 rounded-lg text-sm text-green-700" data-id="element-45">
          Manifold-rabat 10%
        </div>
      </div>
      <div className="mt-3 text-xs text-gray-500" data-id="element-46">
        Priser kan ændre sig ved ændringer i mix/ETA.
      </div>
    </Accordion>;
}